import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@src/container';
import { UniversityService } from '@src/application/universities/usecases';
import type { UpdateUniversityInputDTO } from '@src/application/universities/dto/university.dto';

/**
 * Hook: useUpdateUniversity
 * Updates an existing university
 *
 * @param options - Mutation options
 * @returns Mutation result
 *
 * @example
 * ```typescript
 * const { mutate: updateUniversity, isPending } = useUpdateUniversity({
 *     onSuccess: () => {
 *         console.log('University updated successfully');
 *     },
 *     onError: (error) => {
 *         console.error('Failed to update university:', error);
 *     }
 * });
 *
 * updateUniversity({
 *     universityId: '123',
 *     name: 'Updated University Name',
 *     code: 'UNIV',
 *     description: 'Updated description',
 *     address: 'Updated address',
 *     website: 'https://university.edu'
 * });
 * ```
 */
export function useUpdateUniversity(options?: {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
}) {
    const queryClient = useQueryClient();
    const universityService = container.resolve<UniversityService>('UniversityService');

    return useMutation({
        mutationFn: async (input: UpdateUniversityInputDTO) => {
            try {
                const result = await universityService.updateUniversity(input);
                if (import.meta.env.DEV) {
                    console.log('✅ University updated:', result);
                }
                return result;
            } catch (error) {
                if (import.meta.env.DEV) {
                    console.error('❌ Failed to update university:', error);
                }
                throw error;
            }
        },
        onSuccess: (data) => {
            // Invalidate universities list
            queryClient.invalidateQueries({ queryKey: ['universities'] });
            options?.onSuccess?.(data);
        },
        onError: options?.onError,
    });
}